import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import {
  Upload as UploadIcon,
  Crop as CropIcon,
  Draw as DrawIcon
} from '@mui/icons-material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiUrl } from '../utils/api';

interface SignatureData {
  signature_image_url?: string;
  show_date?: boolean;
  show_name?: boolean;
}

interface SignatureUploadProps {
  companyId: number | null;
  signatureBlock: string;
  onSignatureChange: (signatureData: SignatureData) => void;
  onPendingSignatureChange?: (blob: Blob | null, settings: { show_date: boolean; show_name: boolean }) => void;
  disabled?: boolean;
}

async function getCroppedImg(
  image: HTMLImageElement,
  crop: PixelCrop
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png');
  });
}

const SignatureUpload: React.FC<SignatureUploadProps> = ({
  companyId,
  signatureBlock,
  onSignatureChange,
  onPendingSignatureChange,
  disabled = false
}) => {
  // Parse existing signature data
  const parseSignatureData = (): SignatureData => {
    if (!signatureBlock) return { show_date: true, show_name: true };
    try {
      return JSON.parse(signatureBlock);
    } catch {
      return { show_date: true, show_name: true };
    }
  };

  const initialData = parseSignatureData();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 10,
    y: 25,
    width: 80,
    height: 50
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDate, setShowDate] = useState(initialData.show_date ?? true);
  const [showName, setShowName] = useState(initialData.show_name ?? true);
  const [previewUrl, setPreviewUrl] = useState<string>(initialData.signature_image_url || '');
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please select PNG, JPG, or GIF image.');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File too large. Maximum size is 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
        setCropDialogOpen(true);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
    // Reset input value to allow selecting same file again
    event.target.value = '';
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;

    // Set initial crop for signature (wider aspect ratio)
    const { width, height } = e.currentTarget;
    const cropWidth = width * 0.8;
    const cropHeight = height * 0.5;
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;

    setCrop({
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight
    });
  };

  const handleCropSave = async () => {
    if (!imgRef.current || !completedCrop) {
      setError('Please select a crop area');
      return;
    }

    try {
      setUploading(true);
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);

      if (!croppedBlob) {
        throw new Error('Failed to crop image');
      }

      // Always save blob for later upload (on Save button click)
      if (onPendingSignatureChange) {
        onPendingSignatureChange(croppedBlob, { show_date: showDate, show_name: showName });
      }
      setCropDialogOpen(false);
      setSelectedImage(null);
      setError(null);

      // Create a temporary preview URL
      const tempPreviewUrl = URL.createObjectURL(croppedBlob);
      setPreviewUrl(tempPreviewUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error processing signature');
    } finally {
      setUploading(false);
    }
  };

  const updateSignatureSettings = async (signatureData: SignatureData) => {
    if (!companyId) return;

    try {
      const response = await fetch(apiUrl(`/api/companies/${companyId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          signature_block: JSON.stringify(signatureData)
        })
      });

      if (!response.ok) {
        console.error('Failed to update signature settings');
      }
    } catch (err) {
      console.error('Error updating signature settings:', err);
    }
  };

  const handleSettingChange = async (setting: 'show_date' | 'show_name', value: boolean) => {
    if (setting === 'show_date') {
      setShowDate(value);
    } else {
      setShowName(value);
    }

    // If company exists, update immediately
    if (companyId) {
      const newSignatureData: SignatureData = {
        signature_image_url: previewUrl,
        show_date: setting === 'show_date' ? value : showDate,
        show_name: setting === 'show_name' ? value : showName
      };
      await updateSignatureSettings(newSignatureData);
      onSignatureChange(newSignatureData);
    } else {
      // For new company, just notify parent
      if (onPendingSignatureChange) {
        onPendingSignatureChange(null, {
          show_date: setting === 'show_date' ? value : showDate,
          show_name: setting === 'show_name' ? value : showName
        });
      }
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setSelectedImage(null);
    setCrop({
      unit: '%',
      x: 10,
      y: 25,
      width: 80,
      height: 50
    });
    setCompletedCrop(null);
  };

  const handleCropExisting = () => {
    if (previewUrl && !previewUrl.startsWith('blob:')) {
      setSelectedImage(previewUrl);
      setCropDialogOpen(true);
      setError(null);
    }
  };

  return (
    <Box>
      {/* Upload Button */}
      <Button
        variant="outlined"
        component="label"
        fullWidth
        disabled={disabled || uploading}
        startIcon={<UploadIcon />}
        sx={{ mb: 2 }}
      >
        {uploading ? 'Processing...' : 'Upload Signature/Stamp'}
        <input
          type="file"
          hidden
          accept="image/png,image/jpeg,image/jpg,image/gif"
          onChange={handleFileSelect}
        />
      </Button>

      {/* Settings */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Signature Options
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showDate}
                onChange={(e) => handleSettingChange('show_date', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Show date when signature is applied
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={showName}
                onChange={(e) => handleSettingChange('show_name', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Show user name when signature is applied
              </Typography>
            }
          />
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Signature Preview */}
      {previewUrl && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Current Signature/Stamp:
          </Typography>
          <Box
            sx={{
              mt: 1,
              p: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50'
            }}
          >
            <Box
              component="img"
              src={previewUrl}
              alt="Signature"
              sx={{
                maxWidth: '100%',
                maxHeight: 100,
                objectFit: 'contain'
              }}
            />
            {/* Preview of how it will look */}
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              {showName && (
                <Typography variant="caption" display="block" color="text.secondary">
                  [User Name]
                </Typography>
              )}
              {showDate && (
                <Typography variant="caption" display="block" color="text.secondary">
                  [Date]
                </Typography>
              )}
            </Box>
          </Box>
          {/* Crop existing button - temporarily disabled due to CORS issues with Azure
          {previewUrl && !previewUrl.startsWith('blob:') && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CropIcon />}
              onClick={handleCropExisting}
              disabled={disabled || uploading}
              sx={{ mt: 1 }}
            >
              Crop Current Signature
            </Button>
          )}
          */}
        </Box>
      )}

      {!previewUrl && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50'
          }}
        >
          <DrawIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No signature uploaded
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Upload a signature image or company stamp
          </Typography>
        </Box>
      )}

      {/* Info for new company */}
      {!companyId && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Signature will be uploaded after you save the company
        </Alert>
      )}

      {/* Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={handleCropCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <CropIcon />
              <Typography variant="h6">Crop Signature</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Drag to select crop area
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            {/* Crop Area */}
            <Box sx={{
              maxWidth: '100%',
              maxHeight: 450,
              overflow: 'auto',
              bgcolor: '#f5f5f5',
              borderRadius: 2,
              p: 2
            }}>
              {selectedImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  ruleOfThirds={true}
                  minWidth={50}
                  minHeight={50}
                >
                  <img
                    src={selectedImage}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      display: 'block'
                    }}
                    crossOrigin="anonymous"
                  />
                </ReactCrop>
              )}
            </Box>

            {/* Instructions */}
            <Alert severity="info" sx={{ width: '100%' }}>
              <Typography variant="caption">
                Click and drag to select the signature area.
                Drag corners or edges to resize. Drag inside to move.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCropCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleCropSave}
            variant="contained"
            disabled={uploading || !completedCrop}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Processing...' : 'Save Signature'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignatureUpload;
